
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const UpdatedHeroSection = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Handle body scroll lock for mobile menu
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    // Special handling for schedule - navigate to consultation page
    if (sectionId === 'class-schedule') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate('/consulting');
      setIsMenuOpen(false);
      return;
    }

    // Add small delay to ensure DOM is ready
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerHeight = 80; // Account for fixed header
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
    setIsMenuOpen(false);
  };

  const handleConsultationClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('/consulting');
  };

  const handleCourseClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('/purchase');
  };

  return (
    <section className="relative bg-gradient-to-br from-black via-gray-900 to-black min-h-screen flex items-center">
      {/* Fixed Navigation Menu - Higher z-index */}
      <nav className="fixed top-0 left-0 right-0 z-[100] bg-black/95 backdrop-blur-md border-b border-gray-800">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo - Higher z-index */}
            <Link to="/" className="flex items-center space-x-3 z-[110] relative" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center">
                <i className="ri-car-line text-2xl text-black"></i>
              </div>
              <div className="text-left">
                <div className="font-bold text-xl text-white leading-tight" style={{ fontFamily: '"Pacifico", serif' }}>
                  Georgia Dealer License
                </div>
                <div className="text-sm text-gray-300 leading-tight">Complete Training Program</div>
              </div>
            </Link>

            {/* Desktop Navigation - Evenly Spaced */}
            <div className="hidden lg:flex items-center justify-center flex-1 mx-8">
              <div className="flex items-center space-x-1">
                <button 
                  onClick={() => scrollToSection('overview')}
                  className="flex items-center justify-center h-12 px-6 text-white hover:text-yellow-400 hover:bg-white/20 transition-all font-medium rounded-lg whitespace-nowrap cursor-pointer"
                >
                  Overview
                </button>
                <button 
                  onClick={() => scrollToSection('resources')}
                  className="flex items-center justify-center h-12 px-6 text-white hover:text-yellow-400 hover:bg-white/20 transition-all font-medium rounded-lg whitespace-nowrap cursor-pointer"
                >
                  Resources
                </button>
                <button 
                  onClick={() => scrollToSection('pricing')}
                  className="flex items-center justify-center h-12 px-6 text-white hover:text-yellow-400 hover:bg-white/20 transition-all font-medium rounded-lg whitespace-nowrap cursor-pointer"
                >
                  Pricing
                </button>
                <button 
                  onClick={() => scrollToSection('class-schedule')}
                  className="flex items-center justify-center h-12 px-6 text-white hover:text-yellow-400 hover:bg-white/20 transition-all font-medium rounded-lg whitespace-nowrap cursor-pointer"
                >
                  Schedule
                </button>
                <button 
                  onClick={() => scrollToSection('faq')}
                  className="flex items-center justify-center h-12 px-6 text-white hover:text-yellow-400 hover:bg-white/20 transition-all font-medium rounded-lg whitespace-nowrap cursor-pointer"
                >
                  FAQ
                </button>
                <a 
                  href="http://michael-jackson-s-school7.teachable.com/sign_in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center h-12 px-6 text-white hover:text-yellow-400 hover:bg-white/20 transition-all font-medium rounded-lg whitespace-nowrap cursor-pointer"
                >
                  Login
                </a>
              </div>
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <button 
                onClick={handleConsultationClick}
                className="flex items-center justify-center h-12 px-8 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black rounded-lg font-bold hover:from-yellow-500 hover:to-yellow-600 hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Book Consultation
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Background Image with proper sizing */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20" 
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/a53583cbf68343e91350e93ec4c1cdd1.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      ></div>
      
      {/* Content - Proper spacing from fixed header */}
      <div className="relative container mx-auto px-6 py-32 mt-20 z-10">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
            <span className="block mb-2">Georgia Dealer License</span>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500">
              Consultation
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Get expert guidance and personalized support to obtain your Georgia used car dealer license with our proven consultation services
          </p>

          {/* Social Proof */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 mb-16 text-sm">
            <div className="flex items-center space-x-2">
              <div className="flex -space-x-2">
                <img 
                  alt="Professional business client testimonial" 
                  className="w-8 h-8 rounded-full border-2 border-white object-cover" 
                  src="https://readdy.ai/api/search-image?query=professional%20business%20person%20headshot%20smiling%20confident%20entrepreneur%20successful%20dealer&width=40&height=40&seq=client1&orientation=squarish"
                  width="32"
                  height="32"
                  loading="eager"
                />
                <img 
                  alt="Successful dealer license client" 
                  className="w-8 h-8 rounded-full border-2 border-white object-cover" 
                  src="https://readdy.ai/api/search-image?query=successful%20business%20owner%20professional%20headshot%20confident%20smile%20automotive%20industry&width=40&height=40&seq=client2&orientation=squarish"
                  width="32"
                  height="32"
                  loading="eager"
                />
                <img 
                  alt="Georgia dealer license graduate" 
                  className="w-8 h-8 rounded-full border-2 border-white object-cover" 
                  src="https://readdy.ai/api/search-image?query=professional%20entrepreneur%20headshot%20business%20success%20confident%20car%20dealer&width=40&height=40&seq=client3&orientation=squarish"
                  width="32"
                  height="32"
                  loading="eager"
                />
              </div>
              <span className="text-gray-300">500+ Clients</span>
            </div>
            
            <div className="flex items-center space-x-1">
              <div className="flex text-yellow-400" role="img" aria-label="5 star rating">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
              <span className="text-gray-300">4.9/5 Rating</span>
            </div>
            
            <div className="flex items-center space-x-1">
              <i className="ri-shield-check-line text-green-400"></i>
              <span className="text-gray-300">30-Day Guarantee</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16">
            <button 
              onClick={handleConsultationClick}
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-8 py-4 rounded-full font-bold text-lg hover:from-yellow-500 hover:to-yellow-600 hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              aria-label="Book consultation for Georgia dealer license"
            >
              Book Consultation
              <i className="ri-calendar-line ml-2"></i>
            </button>
            
            <button 
              onClick={handleCourseClick}
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-black transition-all whitespace-nowrap cursor-pointer"
              aria-label="View dealer license course for $97"
            >
              View Course - $97
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>

          {/* Stats */}
          <div className="pt-8 border-t border-gray-700">
            <p className="text-gray-400 text-sm mb-6">Trusted by entrepreneurs across Georgia</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 opacity-60">
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">500+</div>
                <div className="text-xs text-gray-400">Clients</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">95%</div>
                <div className="text-xs text-gray-400">Success Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">10</div>
                <div className="text-xs text-gray-400">Modules</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">6hrs</div>
                <div className="text-xs text-gray-400">Content</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UpdatedHeroSection;
